import React, { useCallback, useContext, useMemo } from 'react'

import { ampli } from '@Analytics/ampli'
import { CheckoutModalContext } from '@Domains/Checkout/Providers/CheckoutModalProvider'
import { PaymentError } from '@Domains/Checkout/Steps/Payment/Components/PaymentError/PaymentError'
import { PaymentResponseCallback } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import {
  getBraintreeRefusalReasonCodeStringV2,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Braintree/utils/BraintreeRefusalReasonCodes'
import {
  useDefaultCheckoutResponse,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Default/hooks/useDefaultCheckoutResponse'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'
import { CheckoutV2FinalizeCartResult } from '@Pages/onestepcheckout'

export interface BraintreeResponseV2 {
  authorizePaymentMethod: {
    clientMutationId: string
    transaction: {
      id: string
      status: string
      statusHistory: [
        {
          GatewayRejectedEvent: {
            gatewayRejectionReason: string
            status: string
          }
          ProcessorDeclinedEvent: {
            declineType: string
            processorResponse: {
              legacyCode: string
              message: string
            }
            status: string
          }
          status: string
        },
      ]
    }
  }
  resultCode?: string
}

export const useBraintreeCheckoutResponse = () => {
  const { storeURL } = useContext(MetadataContext)
  const {
    setModal,
    unSetModal,
  } = useContext(CheckoutModalContext)
  const { handlePaymentSubmitResponse } = useDefaultCheckoutResponse()
  const { t } = useTranslation()

  const hasError = useCallback((finalizeCart: CheckoutV2FinalizeCartResult) => {
    const braintree = (finalizeCart.paymentPayload as unknown as BraintreeResponseV2)
    if (braintree?.authorizePaymentMethod.transaction.status) {
      return ['GATEWAY_REJECTED', 'PROCESSOR_DECLINED'].includes(braintree?.authorizePaymentMethod.transaction.status)
    }
    return !finalizeCart.success
  }, [])

  const handleBraintreeRedirect = useCallback((finalizeCart: CheckoutV2FinalizeCartResult) => {
    if (hasError(finalizeCart)) {
      return false
    }
    if (finalizeCart?.redirect) {
      window.location.href = storeURL('checkout/onepage/success')
      return true
    }
    return false
  }, [hasError, storeURL])

  const handleBraintreeError = useCallback((response: CheckoutV2FinalizeCartResult, paymentType: string) => {
    if (hasError(response) && response) {
      ampli.cofCompletionFailRes({
        payment_method: paymentType,
      })
      setModal(<PaymentError closeModal={unSetModal} error={t(getBraintreeRefusalReasonCodeStringV2(response))} />)
      return true
    }
    return false
  }, [hasError, setModal, t, unSetModal])

  const handleBraintreePaymentSubmitResponse: PaymentResponseCallback = useCallback((paymentType: string, finalizeCart: CheckoutV2FinalizeCartResult) => {
    let res: boolean = handleBraintreeRedirect(finalizeCart)
    res = res || handleBraintreeError(finalizeCart, paymentType)
    res = res || handlePaymentSubmitResponse(paymentType, finalizeCart)
    return res
  }, [handleBraintreeError, handleBraintreeRedirect, handlePaymentSubmitResponse])

  const value = useMemo(() => ({ handleBraintreePaymentSubmitResponse }), [handleBraintreePaymentSubmitResponse])

  return value
}
